import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCcw, Save, DollarSign, Timer, Truck, KeyRound, Palette, Plus, Trash2, Pencil, UserPlus, Copy, Download, Database, Upload, Printer, UserCog } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api as apiClient, API_BASE } from "@/lib/api";
import InvoiceNumberingCard from "@/components/InvoiceNumberingCard";
import FuelSurchargeCard from "@/components/FuelSurchargeCard";
import BatcherIntegrationCard from "@/components/BatcherIntegrationCard";
import PlantConnectorsCard from "@/components/PlantConnectorsCard";
import ConnectorGuideCard from "@/components/ConnectorGuideCard";
import OrgBrandingCard from "@/components/OrgBrandingCard";
import DemoModeCard from "@/components/DemoModeCard";
import AccountingHubCard from "@/components/AccountingHubCard";
import EmergencySmsCard from "@/components/EmergencySmsCard";
import DriverAutomationCard from "@/components/DriverAutomationCard";
import DispatchSmsCard from "@/components/DispatchSmsCard";
import DataHealthCard from "@/components/DataHealthCard";
import CsvImportDialog from "@/components/CsvImportDialog";
import { PourConditionsCard } from "@/components/settings/PourConditionsCard";
import { autoPrintEnabled, setAutoPrint } from "@/lib/autoPrint";
import DirectMigrationCard from "@/components/DirectMigrationCard";
import ActivityLog from "@/components/ActivityLog";

const ROLES = [
  { key: "admin", label: "Plant Admin", hint: "Full access to everything" },
  { key: "plant_manager", label: "Plant Manager", hint: "Operations, fleet, customers, reports, settings" },
  { key: "dispatcher", label: "Dispatcher / Operator", hint: "War Room, dispatch, batch plant, fleet, dockets" },
  { key: "batcher", label: "Batcher / Operator", hint: "Batch plant, inventory, mixes, dockets" },
  { key: "driver", label: "Driver", hint: "Driver app only — active load" },
  { key: "loader", label: "Loader Operator", hint: "iPad Loader Screen only — both lanes, live weights, batch buttons" },
  { key: "accounts", label: "Accounts", hint: "Invoices, statements, pricing, customers, reports" },
];
const NOMINATABLE = ["plant_manager", "dispatcher", "batcher", "accounts"];

function SectionPicker({ role, value, onChange }) {
  const [cat, setCat] = useState(null);
  useEffect(() => { api.get("/auth/permissions").then((r) => setCat(r.data)).catch(() => {}); }, []);
  if (!NOMINATABLE.includes(role) || !cat) return null;
  const defaults = cat.role_defaults[role] || [];
  const custom = Array.isArray(value);
  const selected = custom ? value : defaults;
  const toggle = (k) => onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  const groups = [...new Set(cat.sections.map((s) => s.group))];
  return (
    <div className="border border-industrial p-3 space-y-2" data-testid="section-picker">
      <div className="flex items-center justify-between">
        <Label className="label-tag">Sections this user can open</Label>
        <button type="button" onClick={() => onChange(custom ? null : [...defaults])} data-testid="section-picker-mode"
          className="mono text-[10px] uppercase tracking-widest text-primary-orange hover:underline">
          {custom ? "Use role default" : "Nominate sections"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 max-h-56 overflow-y-auto">
        {groups.map((g) => (
          <div key={g} className="space-y-1">
            <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mt-1">{g}</div>
            {cat.sections.filter((s) => s.group === g).map((s) => (
              <label key={s.key} className={`flex items-center gap-2 text-xs ${custom ? "cursor-pointer" : "opacity-70"}`}>
                <input type="checkbox" disabled={!custom} checked={selected.includes(s.key)} onChange={() => toggle(s.key)}
                  className="h-3.5 w-3.5 accent-primary-orange" data-testid={`section-cb-${s.key.replace(/\//g, "_") || "root"}`} />
                {s.label}
              </label>
            ))}
          </div>
        ))}
      </div>
      <div className="text-[10px] text-muted-industrial">{custom ? `${selected.length} nominated` : "Showing the role's default set — click Nominate sections to customise for this user."}</div>
    </div>
  );
}

function generatePassword(len = 12) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

const DEFAULT_PRICING = {
  delivery_free_km: 25,
  delivery_rate_per_km: 2,
  min_load_m3: 3,
  min_load_charge: 70,
  wait_grace_minutes: 45,
  wait_rate_per_min: 3,
};

const FIELDS = [
  {
    section: "Delivery Zone",
    icon: Truck,
    tint: "text-sky-600",
    rows: [
      { key: "delivery_free_km", label: "Free radius", suffix: "km", hint: "First N km included with the load", step: "0.5" },
      { key: "delivery_rate_per_km", label: "Rate beyond radius", suffix: "$ / km", hint: "One-way surcharge per km over the free radius", step: "0.05" },
    ],
  },
  {
    section: "Small Load",
    icon: DollarSign,
    tint: "text-primary-orange",
    rows: [
      { key: "min_load_m3", label: "Minimum billed size", suffix: "m³", hint: "Any load under this triggers a cartage shortfall.", step: "0.1" },
      { key: "min_load_charge", label: "Cartage shortfall rate", suffix: "$ / m³", hint: "Charged per m³ of shortfall (e.g. 1.0 m³ ordered vs 3 m³ min = 2 m³ × $70 = $140).", step: "0.5" },
    ],
  },
  {
    section: "Wait Time (Demurrage)",
    icon: Timer,
    tint: "text-red-600",
    rows: [
      { key: "wait_grace_minutes", label: "Free grace on-site", suffix: "minutes", hint: "Time allowed before the meter starts", step: "1" },
      { key: "wait_rate_per_min", label: "Demurrage rate", suffix: "$ / min", hint: "Charged per whole minute past grace", step: "0.05" },
    ],
  },
];

function PricingRules({ canEdit }) {
  const [rules, setRules] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/pricing");
      setRules(data);
      setDraft(data);
    } catch (e) {
      toast.error("Failed to load pricing rules");
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (k, v) => {
    const num = v === "" ? "" : parseFloat(v);
    setDraft((d) => ({ ...d, [k]: isNaN(num) ? "" : num }));
  };

  const dirty = draft && rules && FIELDS.some((s) => s.rows.some((r) => Number(draft[r.key]) !== Number(rules[r.key])));

  const save = async () => {
    if (!draft) return;
    for (const s of FIELDS) {
      for (const r of s.rows) {
        const v = draft[r.key];
        if (v === "" || isNaN(v) || v < 0) {
          toast.error(`${r.label} must be a non-negative number`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/pricing", draft);
      setRules(data);
      setDraft(data);
      toast.success("Pricing rules saved — applied instantly to new dockets");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save rules");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (!canEdit) return;
    setDraft({ ...DEFAULT_PRICING });
    toast.info("Defaults staged — press Save to persist");
  };

  const revert = () => setDraft(rules);

  if (!draft) return (
    <div className="card-industrial p-5" data-testid="pricing-rules-loading">
      <div className="label-tag">Pricing Rules</div>
      <div className="text-sm text-muted-industrial mt-2">Loading…</div>
    </div>
  );

  return (
    <div className="card-industrial" data-testid="pricing-rules-card">
      <div className="p-5 border-b border-industrial flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag">Configuration</div>
          <div className="heading text-xl font-black uppercase">Pricing Rules</div>
          <div className="text-xs text-muted-industrial mt-1">
            Live rates used by every new docket, invoice line and wait-time timer. Changes apply immediately — no restart required.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={resetDefaults}
            disabled={!canEdit}
            data-testid="pricing-reset-defaults"
            className="mono uppercase text-xs tracking-widest"
          >
            <RefreshCcw className="w-3 h-3 mr-2" />Restore defaults
          </Button>
          <Button
            onClick={save}
            disabled={!canEdit || !dirty || saving}
            data-testid="pricing-save"
            className="rounded-none bg-primary-orange hover:bg-primary-orange/90 text-white mono uppercase text-xs tracking-widest disabled:opacity-40"
          >
            <Save className="w-3 h-3 mr-2" />{saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-industrial">
        {FIELDS.map((section) => {
          const Icon = section.icon;
          return (
            <div key={section.section} className="p-5 space-y-4" data-testid={`pricing-section-${section.section.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${section.tint}`} />
                <div className="mono uppercase text-xs tracking-widest font-bold">{section.section}</div>
              </div>
              {section.rows.map((r) => (
                <div key={r.key}>
                  <div className="flex items-baseline justify-between">
                    <Label className="label-tag" htmlFor={`pricing-${r.key}`}>{r.label}</Label>
                    <span className="mono text-[10px] text-muted-industrial uppercase tracking-widest">{r.suffix}</span>
                  </div>
                  <Input
                    id={`pricing-${r.key}`}
                    data-testid={`pricing-${r.key}`}
                    type="number"
                    step={r.step}
                    min="0"
                    value={draft[r.key] ?? ""}
                    disabled={!canEdit}
                    onChange={(e) => setField(r.key, e.target.value)}
                    className="rounded-none bg-transparent border-industrial mono font-bold text-lg"
                  />
                  <div className="text-[11px] text-muted-industrial mt-1">{r.hint}</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {dirty && (
        <div className="px-5 py-3 border-t border-industrial bg-primary-orange/5 flex items-center justify-between flex-wrap gap-3" data-testid="pricing-dirty-banner">
          <div className="text-xs mono uppercase tracking-widest text-primary-orange">
            Unsaved changes
          </div>
          <button
            type="button"
            onClick={revert}
            className="text-xs mono uppercase tracking-widest text-muted-industrial hover:text-ink"
            data-testid="pricing-revert"
          >
            Revert
          </button>
        </div>
      )}

      {!canEdit && (
        <div className="px-5 py-3 border-t border-industrial text-xs text-muted-industrial mono uppercase tracking-widest">
          Read-only · Admin or Plant Manager required to edit
        </div>
      )}
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmVal, setConfirmVal] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!current || !next) return toast.error("Fill both password fields");
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirmVal) return toast.error("Confirmation does not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirmVal("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-industrial" data-testid="change-password-card">
      <div className="p-5 border-b border-industrial flex items-center gap-2">
        <KeyRound className="w-4 h-4 text-primary-orange" />
        <div>
          <div className="label-tag">Security</div>
          <div className="heading text-xl font-black uppercase">Change Password</div>
          <div className="text-xs text-muted-industrial mt-1">
            Rotate the seeded / temporary password on your account. Minimum 8 characters, must differ from current.
          </div>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="label-tag" htmlFor="pw-current">Current password</Label>
          <Input
            id="pw-current"
            data-testid="pw-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="rounded-none bg-transparent border-industrial mono"
          />
        </div>
        <div>
          <Label className="label-tag" htmlFor="pw-new">New password</Label>
          <Input
            id="pw-new"
            data-testid="pw-new"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="rounded-none bg-transparent border-industrial mono"
          />
        </div>
        <div>
          <Label className="label-tag" htmlFor="pw-confirm">Confirm new</Label>
          <Input
            id="pw-confirm"
            data-testid="pw-confirm"
            type="password"
            autoComplete="new-password"
            value={confirmVal}
            onChange={(e) => setConfirmVal(e.target.value)}
            className="rounded-none bg-transparent border-industrial mono"
          />
        </div>
      </div>
      <div className="px-5 pb-5">
        <Button
          onClick={submit}
          disabled={saving || !current || !next || !confirmVal}
          data-testid="pw-submit"
          className="rounded-none bg-primary-orange hover:bg-primary-orange/90 text-white mono uppercase text-xs tracking-widest disabled:opacity-40"
        >
          <Save className="w-3 h-3 mr-2" />{saving ? "Saving…" : "Update password"}
        </Button>
      </div>
    </div>
  );
}

function ExtrasLibrary({ canEdit }) {
  const [importOpen, setImportOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [draft, setDraft] = useState({ name: "", color: "#FBBF24", price_per_m3: 0, active: true });
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/extra-tags");
      setTags(data);
    } catch { toast.error("Failed to load extras"); }
  };
  useEffect(() => { load(); }, []);

  const addTag = async () => {
    if (!draft.name.trim()) return toast.error("Name required");
    try {
      await api.post("/extra-tags", {
        name: draft.name.trim(),
        color: draft.color,
        price_per_m3: Number(draft.price_per_m3) || 0,
        active: !!draft.active,
      });
      toast.success(`Added "${draft.name.trim()}"`);
      setDraft({ name: "", color: "#FBBF24", price_per_m3: 0, active: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateTag = async (t, patch) => {
    setBusyId(t.id);
    try {
      await api.put(`/extra-tags/${t.id}`, {
        name: patch.name ?? t.name,
        color: patch.color ?? t.color,
        price_per_m3: patch.price_per_m3 ?? t.price_per_m3,
        active: patch.active ?? t.active,
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const deleteTag = async (t) => {
    if (!window.confirm(`Delete extra "${t.name}"? Existing jobs keep the tag id but the entry will disappear from the library.`)) return;
    setBusyId(t.id);
    try {
      await api.delete(`/extra-tags/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="card-industrial" data-testid="extras-library-card">
      <div className="p-5 border-b border-industrial flex items-center gap-2">
        <Palette className="w-4 h-4 text-primary-orange" />
        <div className="flex-1">
          <div className="label-tag">Library</div>
          <div className="heading text-xl font-black uppercase">Extras & Additives</div>
          <div className="text-xs text-muted-industrial mt-1">
            Fibres, colours, waterproofing, pump — anything a customer can add to a mix. Prices are per m³ and roll into the docket / invoice automatically.
          </div>
        </div>
        {canEdit && <Button data-testid="import-extras-btn" variant="outline" onClick={() => setImportOpen(true)} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold"><Upload className="w-4 h-4 mr-1 text-primary-orange" />Import rates</Button>}
      </div>
      <CsvImportDialog entity="extras" open={importOpen} onOpenChange={setImportOpen} onDone={load} />

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-industrial">
            <tr className="text-left">
              <th className="p-3 label-tag w-16">Color</th>
              <th className="p-3 label-tag">Name</th>
              <th className="p-3 label-tag text-right">$ / m³</th>
              <th className="p-3 label-tag w-24">Active</th>
              <th className="p-3 label-tag w-16"></th>
            </tr>
          </thead>
          <tbody>
            {tags.map((t) => (
              <tr key={t.id} className="border-b border-industrial hover:bg-surface-2" data-testid={`extra-row-${t.id}`}>
                <td className="p-2">
                  <input
                    type="color"
                    value={t.color}
                    disabled={!canEdit || busyId === t.id}
                    onChange={(e) => updateTag(t, { color: e.target.value })}
                    className="w-10 h-8 bg-transparent border border-industrial cursor-pointer"
                    data-testid={`extra-color-${t.id}`}
                  />
                </td>
                <td className="p-2">
                  <Input
                    value={t.name}
                    disabled={!canEdit || busyId === t.id}
                    onBlur={(e) => e.target.value.trim() !== t.name && updateTag(t, { name: e.target.value.trim() })}
                    onChange={(e) => setTags(tags.map((x) => x.id === t.id ? { ...x, name: e.target.value } : x))}
                    data-testid={`extra-name-${t.id}`}
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </td>
                <td className="p-2">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={t.price_per_m3}
                    disabled={!canEdit || busyId === t.id}
                    onBlur={(e) => Number(e.target.value) !== Number(t.price_per_m3) && updateTag(t, { price_per_m3: Number(e.target.value) || 0 })}
                    onChange={(e) => setTags(tags.map((x) => x.id === t.id ? { ...x, price_per_m3: parseFloat(e.target.value) } : x))}
                    data-testid={`extra-price-${t.id}`}
                    className="rounded-none bg-transparent border-industrial mono text-right"
                  />
                </td>
                <td className="p-2 text-center">
                  <button
                    type="button"
                    disabled={!canEdit || busyId === t.id}
                    onClick={() => updateTag(t, { active: !t.active })}
                    data-testid={`extra-active-${t.id}`}
                    className={`px-2 py-1 mono uppercase text-[10px] tracking-widest border ${t.active ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}
                  >
                    {t.active ? "Active" : "Off"}
                  </button>
                </td>
                <td className="p-2 text-right">
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => deleteTag(t)}
                      disabled={busyId === t.id}
                      data-testid={`extra-delete-${t.id}`}
                      className="text-red-500 hover:text-red-400 disabled:opacity-30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {tags.length === 0 && (
              <tr><td colSpan="5" className="p-6 text-center text-muted-industrial">No extras yet. Add one below.</td></tr>
            )}
          </tbody>
          {canEdit && (
            <tfoot className="bg-surface-2">
              <tr>
                <td className="p-2">
                  <input
                    type="color"
                    value={draft.color}
                    onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                    className="w-10 h-8 bg-transparent border border-industrial cursor-pointer"
                    data-testid="new-extra-color"
                  />
                </td>
                <td className="p-2">
                  <Input
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="e.g. Blue Colour, HD Steel Fibre 5%, Waterproof HB300"
                    onKeyDown={(e) => e.key === "Enter" && addTag()}
                    data-testid="new-extra-name"
                    className="rounded-none bg-transparent border-industrial mono"
                  />
                </td>
                <td className="p-2">
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={draft.price_per_m3}
                    onChange={(e) => setDraft({ ...draft, price_per_m3: parseFloat(e.target.value) || 0 })}
                    data-testid="new-extra-price"
                    className="rounded-none bg-transparent border-industrial mono text-right"
                  />
                </td>
                <td className="p-2 text-center">
                  <span className="mono uppercase text-[10px] text-muted-industrial tracking-widest">Auto</span>
                </td>
                <td className="p-2 text-right">
                  <Button
                    onClick={addTag}
                    disabled={!draft.name.trim()}
                    data-testid="add-extra-btn"
                    className="rounded-none bg-primary-orange hover:bg-primary-orange/90 text-white mono uppercase text-[10px] tracking-widest px-2 h-8 disabled:opacity-40"
                  >
                    <Plus className="w-3 h-3 mr-1" />Add
                  </Button>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {!canEdit && (
        <div className="px-5 py-3 border-t border-industrial text-xs text-muted-industrial mono uppercase tracking-widest">
          Read-only · Admin or Plant Manager required to edit
        </div>
      )}
    </div>
  );
}

// v3.5w — Plant Agent token + install helper. Sits on Settings so an admin
// can rotate the token, download the Windows installer, and copy the token
// into config.json without touching a terminal.
function PlantAgentCard() {
  const [token, setToken] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/plant-agent/token");
      setToken(data.token || "");
      setUpdatedAt(data.updated_at || "");
    } catch (e) {
      toast.error("Failed to load plant agent token");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (token && !window.confirm("Generate a fresh token? The old one will stop working — you'll need to paste the new token into config.json on the batcher PC and restart the plant-agent.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/plant-agent/token");
      setToken(data.token || "");
      setUpdatedAt(data.updated_at || "");
      setRevealed(true);
      toast.success("New token generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate token");
    }
    setRotating(false);
  };

  const copyToken = () => {
    if (!token) return;
    navigator.clipboard.writeText(token).then(
      () => toast.success("Token copied to clipboard"),
      () => toast.error("Copy failed — select and copy manually")
    );
  };

  const downloadUrl = `${API_BASE}/plant-agent/download`;
  const setupCmd = `iwr ${window.location.origin}/api/plant-agent/file/setup.ps1 -UseBasicParsing | iex`;
  const copyCmd = () => navigator.clipboard.writeText(setupCmd).then(
    () => toast.success("Command copied — paste it into PowerShell on the plant PC"),
    () => toast.error("Copy failed — select and copy manually")
  );

  return (
    <div className="card-industrial p-5 space-y-4" data-testid="plant-agent-card">
      <div>
        <div className="label-tag">Plant Agent · Rinstrum Scale Bridge</div>
        <div className="heading text-xl font-black uppercase">Live Weight Feed</div>
        <div className="text-xs text-muted-industrial mt-1">
          Small Windows scripts that run on the plant PC: the <b className="text-ink">FELS bridge</b> reads live weights off the
          Rinstrum K412 (TCP 2223) and streams them into the batcher console, the <b className="text-ink">print service</b> sends
          dockets to the yard printer, and the <b className="text-ink">target writer</b> can push batch targets into the panel (TCP 2222).
        </div>
      </div>

      <div className="border border-industrial p-4 bg-surface/40">
        <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-primary-orange mb-2">
          <span className="w-5 h-5 rounded-full bg-primary-orange text-black flex items-center justify-center font-black">1</span>
          Copy this token
        </div>
        <div className="text-xs text-muted-industrial mb-2">
          You'll paste it once into <span className="mono text-ink">armorbatch.env</span> on the plant PC. Every script reads that one
          file, so an update can never wipe it.
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="plant-agent-token"
            readOnly
            value={loading ? "loading…" : (token ? (revealed ? token : "•".repeat(Math.min(40, token.length || 40))) : "no token yet — click Generate")}
            className="rounded-none border-industrial mono text-xs font-mono"
            onFocus={(e) => { setRevealed(true); e.target.select(); }}
          />
          <Button
            variant="outline"
            data-testid="plant-agent-copy"
            onClick={copyToken}
            disabled={!token}
            className="rounded-none border-industrial uppercase text-xs shrink-0"
          >
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy
          </Button>
          <Button
            data-testid="plant-agent-rotate"
            onClick={rotate}
            disabled={rotating}
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold shrink-0"
          >
            <KeyRound className="w-3.5 h-3.5 mr-1" />
            {token ? (rotating ? "Rotating…" : "Rotate") : (rotating ? "Generating…" : "Generate Token")}
          </Button>
        </div>
        {updatedAt && (
          <div className="text-[10px] text-muted-industrial mono mt-1.5">
            Last rotated · {updatedAt.split("T")[0]}
          </div>
        )}
      </div>

      <div className="border border-industrial p-4 bg-surface/40">
        <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-primary-orange mb-2">
          <span className="w-5 h-5 rounded-full bg-primary-orange text-black flex items-center justify-center font-black">2</span>
          Run this one command on the plant PC
        </div>
        <div className="text-xs text-ink mb-2">
          Press the Windows key, type <span className="mono text-primary-orange">powershell</span>, hit Enter, then paste this. It does the
          lot: installs every script into <span className="mono text-primary-orange">C:\GADTECH\plant-agent</span>, fetches SumatraPDF for
          silent printing, sets up autostart + watchdog, and starts the scale feed and print service. Re-run it any time to update or
          restart everything — it never touches your token.
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="plant-agent-setup-cmd"
            readOnly
            value={setupCmd}
            className="rounded-none border-industrial mono text-xs font-mono"
            onFocus={(e) => e.target.select()}
          />
          <Button
            data-testid="plant-agent-copy-cmd"
            onClick={copyCmd}
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold shrink-0"
          >
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy
          </Button>
        </div>
        <div className="text-[10px] text-muted-industrial mono mt-1.5">
          Needs Python 3.11+ installed (python.org, tick "Add to PATH"). It will ask for administrator rights — click Yes. First run opens
          armorbatch.env in Notepad: paste the token above, set SCALE_HOST to the Rinstrum's IP, save, then run the command again.
        </div>
      </div>

      <div className="border border-industrial p-4 bg-surface/40">
        <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-primary-orange mb-2">
          <span className="w-5 h-5 rounded-full bg-primary-orange text-black flex items-center justify-center font-black">3</span>
          What it started, and where to look
        </div>
        <div className="text-xs text-ink space-y-1.5">
          <div><span className="mono text-primary-orange">Scale bridge</span> — live weights off the M4223 on port 2223. The <span className="mono">RINSTRUM · OFFLINE</span> pill flips to <span className="mono text-emerald-400">LIVE</span> within a few seconds. Idle <span className="mono">DUMP</span> records are skipped on purpose.</div>
          <div><span className="mono text-primary-orange">Print service</span> — docket printing with no dialog. Pick your printer in Settings → Printing once it reports in.</div>
          <div><span className="mono text-primary-orange">Watchdog</span> — checks every 10 minutes and restarts whichever service has stopped. Logs live in <span className="mono">C:\GADTECH\plant-agent\logs\</span>.</div>
          <div className="text-muted-industrial">Both services run in the background and start with Windows, so there are no black windows to babysit. If the feed drops in and out, something else is holding port 2223 — the panel accepts only one client, so close any R400 Viewer or spare bridge window. Prefer the manual route? <a href={downloadUrl} download className="text-primary-orange underline">download the zip</a>.</div>
        </div>
      </div>

      <PlantAgentDiagnostics />
    </div>
  );
}

// v3.5y — Live scale connection diagnostics. Polls /plant-agent/status once a
// second and shows the operator exactly why the live weight isn't flowing:
// which scale, TCP-refused vs streaming, and the raw error detail.
function PlantAgentDiagnostics() {
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/plant-agent/status");
        if (alive) { setStatus(data); setErr(""); }
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || e.message || "status fetch failed");
      }
    };
    tick();
    const id = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const online = !!status?.online;
  const scaleStatus = status?.scale_status || [];
  const readings = status?.readings || [];
  const readingById = Object.fromEntries(readings.map((r) => [r.scale_id, r]));

  const stateChip = (state) => {
    const map = {
      streaming: { c: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40", label: "STREAMING" },
      connected: { c: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40", label: "CONNECTED" },
      connecting: { c: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40", label: "CONNECTING" },
      refused: { c: "bg-red-500/20 text-red-400 border-red-500/40", label: "TCP REFUSED" },
      error: { c: "bg-red-500/20 text-red-400 border-red-500/40", label: "ERROR" },
      unknown: { c: "bg-neutral-500/20 text-neutral-400 border-neutral-500/40", label: "UNKNOWN" },
    };
    const cfg = map[state] || map.unknown;
    return <span className={`px-2 py-0.5 border ${cfg.c} mono text-[10px] tracking-widest`}>{cfg.label}</span>;
  };

  return (
    <div className="border border-industrial p-4 bg-surface/40" data-testid="plant-agent-diagnostics">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-primary-orange">
          <span className="w-5 h-5 rounded-full bg-primary-orange text-black flex items-center justify-center font-black">4</span>
          Live Diagnostics
        </div>
        <span
          data-testid="plant-agent-link-state"
          className={`px-2 py-0.5 border mono text-[10px] tracking-widest ${
            online
              ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40"
              : "bg-red-500/20 text-red-400 border-red-500/40"
          }`}
        >
          {online ? "AGENT ONLINE" : "AGENT OFFLINE"}
        </span>
      </div>

      {err && <div className="text-xs text-red-400 mono mb-2">{err}</div>}

      {!online && scaleStatus.length === 0 && (
        <div className="text-xs text-muted-industrial">
          Waiting for the scale feed. On the plant PC open PowerShell and run the command in step 2 above —
          it restarts the bridge and the print service. Check
          <span className="mono text-primary-orange"> SCALE_HOST</span> in
          <span className="mono text-primary-orange"> armorbatch.env</span> is the Rinstrum's IP. Diagnostics populate
          within a few seconds of the first reading.
        </div>
      )}

      {scaleStatus.length > 0 && (
        <div className="space-y-2" data-testid="plant-agent-scale-status">
          {scaleStatus.map((s) => {
            const r = readingById[s.scale_id];
            return (
              <div key={s.scale_id} className="flex items-center gap-3 py-1.5 border-b border-industrial/50 last:border-0">
                <div className="min-w-[110px] mono text-xs text-ink uppercase tracking-widest">{s.label || s.scale_id}</div>
                {stateChip(s.state)}
                <div className="text-xs text-muted-industrial mono flex-1 truncate">{s.detail}</div>
                {r?.weight_kg != null && (
                  <div className="text-right">
                    <div className={`mono text-sm font-black ${r.stale ? "text-zinc-500 line-through" : "text-primary-orange"}`}>
                      {Number(r.weight_kg).toFixed(0)} kg{r.stale ? "" : r.stable ? " ✓" : ""}
                    </div>
                    {r.stale && (
                      <div className="mono text-[9px] uppercase tracking-widest text-amber-400" data-testid={`scale-stale-${s.scale_id}`}>
                        frozen · no update for {Math.round(r.age_seconds)}s
                      </div>
                    )}
                    {Number(r.weight_kg) < -20 && (
                      <div className="mono text-[9px] uppercase tracking-widest text-amber-400" data-testid={`scale-needs-zero-${s.scale_id}`}>
                        reading negative · zero the weigher
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {status?.last_seen && (
        <div className="text-[10px] text-muted-industrial mono mt-3">
          Last agent heartbeat · {new Date(status.last_seen).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

/* Subscription — flat monthly tier rate (AUD). Shown for trial and lapsed plants. */
function BillingCard() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/billing/me").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  if (!info || (!info.tier && !["trial", "lapsed", "active"].includes(info.plan))) return null;

  const hasTier = info.tier && info.monthly_aud != null;
  const start = async () => {
    setBusy(true);
    try {
      const endpoint = hasTier ? "/billing/checkout-tier" : "/billing/checkout";
      const { data } = await api.post(endpoint, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open checkout — please try again or email admin@armorbatch.com");
      setBusy(false);
    }
  };

  const active = info.plan === "active";
  const days = info.trial_ends_at ? Math.ceil((new Date(info.trial_ends_at) - Date.now()) / 86400000) : null;
  const priceLabel = hasTier
    ? `$${Number(info.monthly_aud).toLocaleString()} AUD / month`
    : `$${info.monthly_usd} USD / month`;
  return (
    <div className="card-industrial p-5" data-testid="billing-card">
      <div className="label-tag mb-1">Subscription</div>
      {active ? (
        <div className="mono text-[11px] uppercase tracking-widest text-emerald-500" data-testid="billing-active">
          Active · {hasTier ? `${info.tier_name} plan · ${priceLabel}` : `${info.trucks} trucks · ${priceLabel}`}
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="heading text-3xl font-black">
              ${hasTier ? Number(info.monthly_aud).toLocaleString() : info.monthly_usd}
              <span className="text-sm text-muted-industrial ml-1">{hasTier ? "AUD" : "USD"} / month</span>
            </div>
            <div className="mono text-[11px] uppercase tracking-widest text-muted-industrial">
              {hasTier ? `${info.tier_name} plan` : `${info.trucks} truck${info.trucks === 1 ? "" : "s"} × $${info.rate_usd}`}
            </div>
          </div>
          <div className="text-sm text-muted-industrial mt-2 max-w-2xl">
            {info.plan === "lapsed"
              ? "Your subscription has lapsed — start it again to unlock the plant. Nothing has been deleted."
              : days != null && days >= 0
                ? `Your trial has ${days} day${days === 1 ? "" : "s"} left. Subscribe any time — billing starts today and the plant never stops.`
                : "Your trial has ended. Subscribe to unlock the plant; nothing has been deleted."}
          </div>
          <Button onClick={start} disabled={busy} data-testid="billing-subscribe-btn"
            className="mt-3 rounded-none bg-blue-700 hover:bg-blue-800 text-white mono text-[10px] uppercase tracking-widest font-black">
            {busy ? "Opening checkout…" : "Subscribe with card"}
          </Button>
          <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mt-2">
            Secure checkout by Stripe · cancel any time
          </div>
        </>
      )}
    </div>
  );
}

/* Trial plants arrive pre-loaded with a sample plant — this wipes it when they go live. */
function SampleDataCard() {
  const { org } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  if (org?.plan !== "trial") return null;

  const clear = async () => {
    if (!window.confirm("Delete all sample customers, trucks, mixes, stock and jobs? Your own data is not touched.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/org/clear-sample-data");
      const n = Object.values(data.removed || {}).reduce((a, b) => a + b, 0);
      setDone(n);
      toast.success(`Sample data cleared · ${n} records removed`);
    } catch (e) { toast.error("Could not clear the sample data"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-industrial p-5" data-testid="sample-data-card">
      <div className="label-tag mb-1">Sample plant</div>
      <div className="text-sm text-muted-industrial max-w-2xl">
        Your trial came pre-loaded with sample customers, trucks, mixes, stock and today's jobs so you could
        look around. Clear them when you're ready to run your own — nothing you've created yourself is touched.
      </div>
      {done !== null ? (
        <div className="mono text-[11px] uppercase tracking-widest text-emerald-500 mt-3">Cleared · {done} sample records removed</div>
      ) : (
        <Button onClick={clear} disabled={busy} data-testid="clear-sample-data-btn"
          className="mt-3 rounded-none bg-primary-orange text-black mono text-[10px] uppercase tracking-widest font-black">
          {busy ? "Clearing…" : "Clear sample data and go live"}
        </Button>
      )}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEditPricing = user?.role === "admin" || user?.role === "plant_manager";

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="settings-page">
      <div><div className="label-tag">CONFIGURATION</div><h1 className="heading text-3xl font-black uppercase">Settings</h1></div>

      {user?.is_super_admin && <BillingCard />}
      {user?.is_super_admin && <SampleDataCard />}
      {user?.is_super_admin && <DemoModeCard />}
      {(isAdmin || user?.role === "plant_manager") && <OrgBrandingCard />}

      <PricingRules canEdit={canEditPricing} />

      <ExtrasLibrary canEdit={canEditPricing} />

      {(isAdmin || user?.role === "plant_manager" || user?.role === "dispatcher") && (
        <PourConditionsCard canEdit={isAdmin || user?.role === "plant_manager"} />
      )}
      {(isAdmin || user?.role === "plant_manager") && <InvoiceNumberingCard />}
      {(isAdmin || user?.role === "plant_manager" || user?.role === "accounts") && <FuelSurchargeCard />}
      {user?.is_super_admin && <BatcherIntegrationCard />}
      {user?.is_super_admin && <ConnectorGuideCard />}
      {user?.is_super_admin && <AutoPrintCard />}
      {user?.is_super_admin && <ScaleWriteCard />}
      {user?.is_super_admin && <PlantConnectorsCard />}
      {(isAdmin || user?.role === "plant_manager" || user?.role === "accounts") && <AccountingHubCard />}
      {user?.is_super_admin && <DispatchSmsCard canEdit={isAdmin || user?.role === "plant_manager"} />}
      {user?.is_super_admin && <DriverAutomationCard />}

      {user?.is_super_admin && <EmergencySmsCard canEdit={isAdmin || user?.role === "plant_manager"} />}
      {user?.is_super_admin && <DirectMigrationCard onDone={() => window.dispatchEvent(new CustomEvent("data-health:refresh"))} />}
      {user?.is_super_admin && <DataHealthCard />}

      {user?.is_super_admin && <PlantAgentCard />}

      <ChangePasswordCard />

      <div className="card-industrial p-5">
        <div className="label-tag mb-3">Current User</div>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div><div className="label-tag">Name</div><div>{user?.name}</div></div>
          <div><div className="label-tag">Email</div><div className="mono">{user?.email}</div></div>
          <div><div className="label-tag">Role</div><div className="mono uppercase">{user?.role?.replace("_", " ")}</div></div>
        </div>
      </div>

      <TeamMembersCard isAdmin={isAdmin} currentUserId={user?.id} />

      {user?.is_super_admin && (
        <div className="card-industrial p-5" data-testid="settings-activity-card">
          <div className="label-tag mb-4">Login Activity</div>
          <ActivityLog />
        </div>
      )}

      {user?.is_super_admin && <DataBackupCard />}
      {user?.is_super_admin && <RestoreBackupCard />}
    </div>
  );
}

function DataBackupCard() {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      // Fetch via axios so the cookie/JWT is included, then trigger a download
      const r = await apiClient.get("/admin/export/backup.html", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      a.href = url;
      a.download = `gadtech-backup-${stamp}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — open the HTML file in any browser");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to build backup");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-industrial p-5" data-testid="data-backup-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-tag flex items-center gap-1.5"><Database className="w-3 h-3" /> Data Backup</div>
          <div className="heading text-xl font-black uppercase mt-1">Download Full Backup</div>
          <div className="text-xs text-muted-industrial mt-2 max-w-xl">
            Single self-contained HTML file with every customer, job, docket, invoice, batch, mix, truck, driver,
            pump company, portal user, inventory item, maintenance record and settings row. Works offline in any
            browser · prints to PDF · sensitive fields (passwords, reset tokens) redacted.
          </div>
        </div>
        <Button
          data-testid="download-backup-btn"
          onClick={download}
          disabled={busy}
          className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold disabled:opacity-40"
        >
          <Download className="w-4 h-4 mr-2" />
          {busy ? "Building…" : "Download HTML backup"}
        </Button>
      </div>
    </div>
  );
}

// Must cover every material the panel can hold a setpoint for. 10 mm was missing, so
// a plant running a 14/10/dust/sand mix had nowhere to map its second aggregate.
// Plant bin order confirmed on site: 1 · 20mm, 2 · 14mm, 3 · 10mm, 4 · 7mm, 5 · dust, 6 · sand.
const SCALE_TARGET_KEYS = [
  ["agg_20mm", "Bin 1 · Agg 20mm"], ["agg_14mm", "Bin 2 · Agg 14mm"], ["agg_10mm", "Bin 3 · Agg 10mm"],
  ["agg_7mm", "Bin 4 · Agg 7mm"], ["man_dust", "Bin 5 · Man Dust / Fines"], ["sand", "Bin 6 · Sand"],
  ["cement", "Cement"], ["water", "Water"],
];

function ScaleWriteCard() {
  const [cfg, setCfg] = useState({ mode: "dry_run", address: "20", registers: {}, agent: null });
  const [cmds, setCmds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState({ register: "", value: "" });
  const [sweep, setSweep] = useState({ agg_14mm: "", agg_10mm: "", man_dust: "", sand: "", agg_20mm: "", agg_7mm: "", cement: "", water: "" });
  const runSweep = async () => {
    const targets = Object.fromEntries(Object.entries(sweep).filter(([, v]) => v !== "" && !isNaN(Number(v))).map(([k, v]) => [k, Number(v)]));
    if (!Object.keys(targets).length) return toast.error("Type at least one target as shown on the panel");
    setBusy(true);
    try {
      const { data } = await api.post("/scale/registers/sweep", { targets });
      toast.success(`Sweep queued — the writer will read ${data.registers} registers (read only, ~5-10 min)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not queue sweep"); } finally { setBusy(false); }
  };
  const lastSweep = cmds.find((c) => c.sweep && c.status === "done");
  const [rocks, setRocks] = useState([]);
  useEffect(() => { api.get("/inventory").then((r) => setRocks((r.data || []).filter((i) => i.category === "exposed_rock"))).catch(() => {}); }, []);
  const saveMaterials = (next) => save({ materials: Object.fromEntries(Object.entries(next).filter(([, v]) => v !== "" && v != null)) });
  const load = useCallback(() => {
    api.get("/scale/settings").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/scale/commands?limit=12").then((r) => setCmds(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/scale/settings", { mode: cfg.mode, address: cfg.address, registers: cfg.registers, exposed_register: cfg.exposed_register, ...patch });
      setCfg((c) => ({ ...c, ...data }));
      toast.success(patch.mode === "live" ? "Scale writing is now LIVE" : "Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const scan = async () => {
    setBusy(true);
    try { await api.post("/scale/registers/scan", {}); toast.success("Register scan queued — the writer agent will read them back"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not queue scan"); } finally { setBusy(false); }
  };
  const testWrite = async () => {
    setBusy(true);
    try {
      await api.post("/scale/registers/test-write", { register: test.register, value: Number(test.value) });
      toast.success(`Queued: write ${test.value} to ${test.register} — watch the panel`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not queue the test write");
    } finally { setBusy(false); }
  };

  const seen = cfg.agent?.last_seen ? (Date.now() - new Date(cfg.agent.last_seen).getTime()) / 1000 : null;
  const online = seen !== null && seen < 60;
  const lastScan = cmds.find((c) => c.type === "scan" && c.readings?.length);

  return (
    <div className="card-industrial p-5" data-testid="scale-write-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tag">Scale writing · Rinstrum port 2222</div>
          <div className="mono text-[11px] text-muted-industrial mt-1 uppercase leading-relaxed">
            Lets a batcher push the batch targets straight into the Rinstrum panel instead of typing them.
            Starts in dry run — the agent reports the exact commands it would send and writes nothing.
          </div>
        </div>
        <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${online ? "border-emerald-500 text-emerald-400" : "border-industrial text-muted-industrial"}`} data-testid="scale-writer-status">
          {online ? `Writer online · ${cfg.agent?.host || ""}` : "Writer not connected"}
        </span>
      </div>

      <div className="flex items-center gap-2 mt-4 flex-wrap">
        {["dry_run", "live"].map((m) => (
          <button key={m} type="button" disabled={busy} onClick={() => save({ mode: m })} data-testid={`scale-mode-${m}`}
            className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${cfg.mode === m ? (m === "live" ? "bg-red-600 border-red-600 text-white font-black" : "bg-primary-orange border-primary-orange text-black font-black") : "border-industrial text-muted-industrial hover:text-ink"}`}>
            {m === "live" ? "Live writing" : "Dry run"}
          </button>
        ))}
        <Button variant="outline" onClick={scan} disabled={busy} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold ml-auto" data-testid="scale-scan-btn">
          Read registers off the scale
        </Button>
      </div>
      {cfg.mode === "live" && (
        <div className="mono text-[10px] uppercase tracking-widest text-red-500 border border-red-500 px-2 py-1 mt-2">
          Live — pressing “Send targets to scale” will write to the panel. Writes are refused while the scale is in motion.
        </div>
      )}

      {lastScan && (
        <div className="mt-4">
          <div className="label-tag">Setpoints read off the panel · {new Date(lastScan.finished_at || lastScan.created_at).toLocaleString()}</div>
          <div className="mono text-[10px] text-muted-industrial uppercase mt-1 leading-relaxed">
            C422–CD22 are the STAGE 1–10 recipe targets of the product the panel is running (the writer selects
            it over comms first, via B00F). C407–CD07 show which material number each stage fills (MAT 1 = 0).
            Match each stage to its material below. 0026/0027 are gross/net weight — read only, link check.
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-1 mt-2">
            {lastScan.readings.filter((r) => !/^C[4-9A-D]07$/.test(r.register) && !/^C0[89][0-9A-F]$/.test(r.register)).map((r) => {
              const matName = (n) => { const rr = lastScan.readings.find((x) => x.register === `C${(0x80 + n).toString(16).toUpperCase().padStart(3, "0")}`); return rr?.raw?.includes(":") ? rr.raw.split(":")[1].trim() : ""; };
              const writable = /^(B08[0-7]|C[4-9A-D]22)$/.test(r.register);
              const stage = /^C([4-9A-D])22$/.test(r.register) ? parseInt(r.register[1], 16) - 3 : null;
              const matReading = stage ? lastScan.readings.find((m) => m.register === `C${(stage + 3).toString(16).toUpperCase()}07`) : null;
              const matNo = matReading && matReading.value != null ? Number(matReading.value) : null;
              const assignedTo = stage && matNo ? SCALE_TARGET_KEYS.find(([k]) => (cfg.materials || {})[k] === matNo) : null;
              return (
                <div key={r.register} className={`border px-2 py-1.5 ${assignedTo ? "border-emerald-600" : "border-industrial"}`} data-testid={`scale-reg-${r.register}`}>
                  <div className="mono text-[10px] flex items-center gap-1">
                    <span className="text-primary-orange">{r.register}{stage ? ` · stage ${stage}` : ""}{matNo ? ` · MAT ${matNo}` : ""}</span>
                    <span className="text-ink font-bold">{r.value ?? (r.raw && r.raw.includes(":") && !/^[Cc]/.test(r.raw) ? r.raw.split(":")[1].trim() : "—")}</span>
                    {!writable && <span className="text-muted-industrial ml-auto">{({ B000: "active product #", B006: "active product name", B010: "selected product #", B013: "selected product name" })[r.register] || "read only"}</span>}
                  </div>
                  {stage && matNo && (
                    <div className="mono text-[9px] uppercase text-muted-industrial mt-1">
                      {matName(matNo) || `K412 material ${matNo}`}{SCALE_TARGET_KEYS.find(([k]) => (cfg.materials || {})[k] === matNo) ? ` → ${SCALE_TARGET_KEYS.find(([k]) => (cfg.materials || {})[k] === matNo)[1]}` : " → not mapped"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4" data-testid="scale-prove-box">
          <div className="label-tag">Prove / clear a single stage target</div>
          <div className="flex items-end gap-2 mt-2 flex-wrap">
            <div>
              <Label className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Prove a setpoint · register</Label>
              <Input value={test.register} onChange={(e) => setTest((t) => ({ ...t, register: e.target.value.toUpperCase() }))}
                placeholder="C922" data-testid="scale-test-register" className="rounded-none bg-transparent border-industrial mono h-8 text-xs w-24" />
            </div>
            <div>
              <Label className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Value</Label>
              <Input value={test.value} onChange={(e) => setTest((t) => ({ ...t, value: e.target.value }))}
                placeholder="1234" data-testid="scale-test-value" className="rounded-none bg-transparent border-industrial mono h-8 text-xs w-24" />
            </div>
            <Button variant="outline" onClick={testWrite} disabled={busy || !test.register || !test.value}
              className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold" data-testid="scale-test-write-btn">
              Write it &amp; read it back
            </Button>
            <div className="mono text-[10px] text-muted-industrial uppercase max-w-md leading-relaxed">
              Writes one stage target on an IDLE plant and reads it straight back (stage 1 = C422 … stage 6 = C922,
              stage 8 = CB22). Refused if the scale is moving.
            </div>
          </div>
      </div>

      <div className="label-tag mt-6">Find the target registers · read-only sweep</div>
      <div className="mono text-[10px] text-muted-industrial uppercase mt-1 max-w-3xl leading-relaxed">
        Type the target weights the Rinstrum is showing right now for each material. The writer reads every register
        (0000–03FF, A000–A3FF and B000–B0FF, also matching ×10 / ×100 scaled values) without writing anything and flags the ones holding those exact numbers.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mt-3" data-testid="scale-sweep-form">
        {[["agg_20mm", "20mm"], ["agg_14mm", "14mm"], ["agg_10mm", "10mm"], ["agg_7mm", "7mm"], ["man_dust", "Dust"], ["sand", "Sand"], ["cement", "Cement"], ["water", "Water"]].map(([k, label]) => (
          <div key={k}>
            <Label className="mono text-[9px] uppercase tracking-widest text-muted-industrial">{label} kg</Label>
            <Input value={sweep[k]} onChange={(e) => setSweep((t) => ({ ...t, [k]: e.target.value }))} placeholder="—"
              data-testid={`scale-sweep-${k}`} className="rounded-none bg-transparent border-industrial mono h-8 text-xs" />
          </div>
        ))}
      </div>
      <Button variant="outline" onClick={runSweep} disabled={busy} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold mt-3" data-testid="scale-sweep-btn">
        Sweep registers (read only)
      </Button>
      {lastSweep && (
        <div className="mt-3 border border-industrial p-3" data-testid="scale-sweep-result">
          <div className="mono text-[10px] uppercase text-muted-industrial">Last sweep · {new Date(lastSweep.finished_at).toLocaleString()}</div>
          {(lastSweep.matches || []).length
            ? <div className="flex flex-wrap gap-2 mt-2">{lastSweep.matches.map((m, i) => (
                <button type="button" key={i} data-testid={`scale-sweep-match-${m.register}`}
                  onClick={() => setTest({ register: m.register, value: String(Math.round(m.value)) })}
                  className="mono text-xs px-2 py-1 border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10">
                  {m.register} = {m.material} ({m.value} kg)
                </button>))}
                <div className="mono text-[10px] text-muted-industrial uppercase w-full mt-1">Tap a match to load it into "Prove a setpoint", then write a different number and watch the panel.</div>
              </div>
            : <div className="mono text-xs text-primary-orange mt-2">{lastSweep.detail}</div>}
        </div>
      )}

      {lastScan && lastScan.readings.some((r) => /^C0[89][0-9A-F]$/.test(r.register) && r.raw?.includes(":")) && (
        <div className="mt-3" data-testid="scale-material-names">
          <div className="label-tag">Materials on the panel (C081–C094)</div>
          <div className="flex flex-wrap gap-1 mt-1">
            {lastScan.readings.filter((r) => /^C0[89][0-9A-F]$/.test(r.register) && r.raw?.includes(":") && r.raw.split(":")[1].trim()).map((r) => (
              <span key={r.register} className="mono text-[10px] px-2 py-0.5 border border-industrial">MAT {parseInt(r.register.slice(1), 16) - 0x80} · {r.raw.split(":")[1].trim()}</span>
            ))}
          </div>
        </div>
      )}
      <div className="label-tag mt-4">Fill stages on the K412</div>
      <div className="mono text-[10px] text-muted-industrial uppercase mt-1 max-w-3xl leading-relaxed">
        Tick every FILL stage in the panel's BATCH → STAGES list (not the DUMP stage). On each send, ArmorBatch lays the
        mix onto these stages in loader order — 20mm → 14mm → 10mm → 7mm → dust → sand → exposed rocks — writing the
        material and the target for each and zeroing the rest. A mix with more materials than fill stages is refused.
      </div>
      <div className="flex flex-wrap gap-2 mt-1" data-testid="scale-active-stages">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
          const on = (cfg.active_stages || []).includes(n);
          return (
            <button type="button" key={n} data-testid={`scale-stage-${n}`}
              onClick={() => { const next = on ? (cfg.active_stages || []).filter((x) => x !== n) : [...(cfg.active_stages || []), n].sort((a, b) => a - b); setCfg((c) => ({ ...c, active_stages: next })); save({ active_stages: next }); }}
              className={`mono text-[10px] px-3 py-1.5 border ${on ? "border-emerald-500 text-emerald-400 bg-emerald-500/10" : "border-industrial text-muted-industrial line-through"}`}>
              stage {n}
            </button>
          );
        })}
      </div>

      <div className="label-tag mt-4">Loader order on the fill stages</div>
      <div className="mono text-[10px] text-muted-industrial uppercase mt-1 max-w-3xl leading-relaxed">
        Stage 1 gets the first material below that the mix contains, stage 2 the next, and so on. Use the arrows to match how the loader works the bins.
      </div>
      <div className="flex flex-wrap gap-1.5 mt-1" data-testid="scale-stage-order">
        {(cfg.stage_order || []).map((k, i, arr) => {
          const label = k === "exposed" ? "Exposed rocks" : (SCALE_TARGET_KEYS.find(([kk]) => kk === k)?.[1] || k);
          const move = (d) => { const next = [...arr]; const j = i + d; if (j < 0 || j >= next.length) return; [next[i], next[j]] = [next[j], next[i]]; setCfg((c) => ({ ...c, stage_order: next })); save({ stage_order: next }); };
          return (
            <div key={k} className="inline-flex items-center border border-industrial mono text-[10px]" data-testid={`scale-order-${k}`}>
              <button type="button" onClick={() => move(-1)} disabled={i === 0} className="px-1.5 py-1 hover:text-primary-orange disabled:opacity-30">‹</button>
              <span className="px-1.5 py-1 uppercase tracking-widest"><span className="text-muted-industrial">{i + 1}·</span> {label}</span>
              <button type="button" onClick={() => move(1)} disabled={i === arr.length - 1} className="px-1.5 py-1 hover:text-primary-orange disabled:opacity-30">›</button>
            </div>
          );
        })}
      </div>

      <div className="label-tag mt-4">K412 material numbers</div>
      <div className="mono text-[10px] text-muted-industrial uppercase mt-1 max-w-3xl leading-relaxed">
        The panel's BATCH → MAT list (MAT 1 = 20MM AGG, MAT 2 = 14MM AGG …). Recipes differ per product, so the
        writer reads which material each stage fills off the active recipe and puts every target into the right
        stage — a material split over two stages keeps its proportions.
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mt-1">
        {SCALE_TARGET_KEYS.map(([key, label]) => (
          <div key={key}>
            <Label className="mono text-[9px] uppercase tracking-widest text-muted-industrial">{label}</Label>
            <Input
              type="number" min={1} max={20}
              value={cfg.materials?.[key] ?? ""}
              onChange={(e) => setCfg((c) => ({ ...c, materials: { ...c.materials, [key]: e.target.value === "" ? "" : Number(e.target.value) } }))}
              onBlur={() => saveMaterials(cfg.materials || {})}
              placeholder="MAT #"
              data-testid={`scale-material-${key}`}
              className="rounded-none bg-transparent border-industrial mono h-8 text-xs"
            />
          </div>
        ))}
      </div>

      {rocks.length > 0 && (
        <>
          <div className="label-tag mt-4">Exposed / decorative rocks · K412 material numbers</div>
          <div className="mono text-[10px] text-muted-industrial uppercase mt-1">
            One per rock (e.g. Ice 10mm = 7, Cal 10mm = 8, W/Dust = 9, B/Cream = 10). A rock left blank falls back to the number below.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mt-1" data-testid="scale-rock-materials">
            {rocks.map((r) => { const key = `exposed:${r.sku || r.name}`; return (
              <div key={r.id}>
                <Label className="mono text-[9px] uppercase tracking-widest text-muted-industrial truncate block">{r.name}</Label>
                <Input type="number" min={1} max={20} placeholder="MAT #" data-testid={`scale-rock-${r.id}`}
                  value={cfg.materials?.[key] ?? ""}
                  onChange={(e) => setCfg((c) => ({ ...c, materials: { ...c.materials, [key]: e.target.value === "" ? "" : Number(e.target.value) } }))}
                  onBlur={() => saveMaterials(cfg.materials || {})}
                  className="rounded-none bg-transparent border-industrial mono h-8 text-xs" />
              </div>); })}
          </div>
        </>
      )}

      <div className="label-tag mt-4">Fallback K412 material for unmapped exposed rock</div>
      <div className="mono text-[10px] text-muted-industrial uppercase mt-1">
        Leave blank (recommended): a rock with no number above blocks the send instead of being written as the wrong material.
      </div>
      <Input type="number" min={0} max={20} data-testid="scale-exposed-mat" placeholder="none"
        value={cfg.exposed_mat || ""}
        onChange={(e) => setCfg((c) => ({ ...c, exposed_mat: e.target.value === "" ? 0 : Number(e.target.value) }))}
        onBlur={() => save({ exposed_mat: cfg.exposed_mat || 0 })}
        className="mt-1 rounded-none bg-transparent border-industrial mono h-8 text-xs w-full sm:w-40" />

      {cmds.length > 0 && (
        <div className="mt-4 space-y-1">
          <div className="flex items-center">
            <div className="label-tag">Recent scale commands</div>
            {cmds.some((c) => c.status === "queued") && (
              <button type="button" disabled={busy} data-testid="scale-cancel-queued-btn"
                onClick={async () => { setBusy(true); try { const { data } = await api.delete("/scale/commands/queued"); toast.success(`Cancelled ${data.cancelled} queued command(s)`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); } finally { setBusy(false); } }}
                className="ml-auto mono text-[10px] uppercase tracking-widest px-2 py-1 border border-red-500/60 text-red-400 hover:bg-red-500/10 disabled:opacity-60">
                Cancel queued
              </button>
            )}
          </div>
          {cmds.map((c) => (
            <div key={c.id} className="mono text-[10px] uppercase flex items-center gap-2 border-b border-industrial/60 py-1" data-testid={`scale-cmd-${c.id}`}>
              <span className={c.status === "done" ? "text-emerald-400" : c.status === "failed" ? "text-red-500" : "text-primary-orange"}>{c.status}</span>
              <span className="text-muted-industrial">{c.type} · {c.mode}</span>
              <span className="ml-auto text-muted-industrial truncate max-w-[55%]">{c.detail || `${c.line_count ?? (c.lines || []).length} line(s)`}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mono text-[10px] text-muted-industrial mt-3 uppercase">
        Run plant-agent/run-rinstrum-writer.bat on the plant PC. Port 2222 = commands, 2223 = the read-only weight stream.
      </div>
    </div>
  );
}


function AutoPrintCard() {
  const [on, setOn] = useState(autoPrintEnabled());
  const [sp, setSp] = useState({ enabled: false, printer: "", copies: 1, auto_queue: false, duplex: "long", agent: null });
  const [jobs, setJobs] = useState([]);
  const failedCount = jobs.filter((j) => j.status === "failed").length;
  const [saving, setSaving] = useState(false);
  const loadSp = useCallback(() => { api.get("/settings/silent-print").then((r) => setSp(r.data)).catch(() => {}); api.get("/print-jobs?limit=40").then((r) => setJobs(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadSp(); const i = setInterval(loadSp, 10000); return () => clearInterval(i); }, [loadSp]);
  const toggle = (v) => { setAutoPrint(!!v); setOn(!!v); toast.success(v ? "Auto-print on — dockets will open in a new tab on this device when a load completes" : "Auto-print off"); };
  const saveSp = async (patch) => {
    const next = { enabled: sp.enabled, printer: sp.printer, copies: sp.copies, auto_queue: sp.auto_queue, ...patch };
    setSaving(true);
    try { const { data } = await api.put("/settings/silent-print", next); setSp((p) => ({ ...p, ...data })); toast.success(data.enabled ? `Silent print on → "${data.printer}"` : "Silent print off"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const retryFailed = async () => {
    try {
      const { data } = await api.post("/connectors/print-jobs/retry-failed");
      toast.success(data.requeued ? `${data.requeued} docket${data.requeued === 1 ? "" : "s"} back in the queue` : "No failed dockets to retry");
      loadSp();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not requeue"); }
  };
  const testPrint = async () => { try { const { data } = await api.post("/print-jobs/test"); toast.success(`Test print queued · docket ${data.docket_no}`); loadSp(); } catch (e) { toast.error(e.response?.data?.detail || "Could not queue test"); } };
  const seen = sp.agent?.last_seen ? (Date.now() - new Date(sp.agent.last_seen).getTime()) / 1000 : null;
  const agentOk = seen !== null && seen < 60;
  const detected = sp.agent?.printers || [];
  return (
    <div className="card-industrial p-5 space-y-5" data-testid="auto-print-card">
      <label className="flex items-start gap-4 cursor-pointer">
        <Checkbox checked={on} onCheckedChange={toggle} className="mt-0.5 h-5 w-5 rounded-none border-industrial" data-testid="auto-print-toggle" />
        <div>
          <div className="heading text-sm font-black uppercase tracking-wider">Auto-print docket on load completed</div>
          <div className="mono text-[11px] text-muted-industrial mt-1 uppercase leading-relaxed">
            Opens the docket PDF in a new browser tab the instant a load finishes batching, so the yard printer prints without a manual click. Customers can be excluded individually via "Auto-print docket" on their customer file.
          </div>
          <div className="mono text-[10px] text-muted-industrial/70 mt-1">Saved per device — turn it on only on the computer connected to the yard printer. If the tab is blocked, allow pop-ups for this site once.</div>
        </div>
      </label>
      <div className="border-t border-industrial pt-5" data-testid="silent-print-section">
        <div className="flex items-start gap-4">
          <Checkbox checked={sp.enabled} disabled={saving} onCheckedChange={(v) => { if (v && !sp.printer.trim()) return toast.error("Enter the printer name first"); saveSp({ enabled: !!v }); }} className="mt-0.5 h-5 w-5 rounded-none border-industrial" data-testid="silent-print-toggle" />
          <div className="flex-1">
            <div className="heading text-sm font-black uppercase tracking-wider">Silent print via plant agent <span className="ml-2 mono text-[9px] px-1.5 py-0.5 border border-primary-orange text-primary-orange">No browser · no dialog</span></div>
            <div className="mono text-[11px] text-muted-industrial mt-1 uppercase leading-relaxed">
              The tiny print service on the yard PC (plant-agent/print_agent.py) sends the docket PDF straight to the named printer — no browser, no dialog. Applies to the whole plant, not just this device.
            </div>
            <div className="grid sm:grid-cols-[1fr_120px_150px_auto] gap-3 mt-3 items-end">
              <div><Label className="label-tag">Printer {detected.length > 0 ? `· detected on ${sp.agent?.host || "yard PC"}` : "(exactly as in Windows / CUPS)"}</Label>
                {detected.length > 0 ? (
                  <select
                    value={sp.printer}
                    onChange={(e) => saveSp({ printer: e.target.value })}
                    className="w-full h-10 px-2 rounded-none bg-transparent border border-industrial mono text-sm"
                    data-testid="silent-print-printer-select"
                  >
                    <option value="">— select a printer —</option>
                    {detected.map((p) => <option key={p} value={p}>{p}{p === sp.agent?.default_printer ? " (default)" : ""}</option>)}
                    {sp.printer && !detected.includes(sp.printer) && <option value={sp.printer}>{sp.printer} (not on this PC)</option>}
                  </select>
                ) : (
                  <Input value={sp.printer} onChange={(e) => setSp({ ...sp, printer: e.target.value })} onBlur={() => saveSp({})} placeholder="e.g. Canon GX3000 series" className="rounded-none bg-transparent border-industrial mono" data-testid="silent-print-printer" />
                )}
              </div>
              <div><Label className="label-tag">Copies</Label>
                <Input type="number" min="1" max="5" value={sp.copies} onChange={(e) => setSp({ ...sp, copies: Number(e.target.value) || 1 })} onBlur={() => saveSp({})} className="rounded-none bg-transparent border-industrial mono" data-testid="silent-print-copies" /></div>
              <div><Label className="label-tag">Double sided</Label>
                <select
                  value={sp.duplex || "long"}
                  onChange={(e) => saveSp({ duplex: e.target.value })}
                  className="w-full h-10 px-2 rounded-none bg-transparent border border-industrial mono text-sm"
                  data-testid="silent-print-duplex"
                >
                  <option value="long">On · flip long edge</option>
                  <option value="short">On · flip short edge</option>
                  <option value="off">Off · single sided</option>
                </select>
              </div>
              <Button variant="outline" onClick={testPrint} disabled={!sp.printer} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold" data-testid="silent-print-test"><Printer className="w-4 h-4 mr-1 text-primary-orange" />Send test print</Button>
              {failedCount > 0 && (
                <Button variant="outline" onClick={retryFailed} className="rounded-none border-amber-500/60 text-amber-400 hover:text-amber-300 uppercase tracking-widest text-xs font-bold" data-testid="silent-print-retry-failed">
                  Retry {failedCount} failed
                </Button>
              )}
            </div>
            <label className="flex items-start gap-3 mt-3 cursor-pointer">
              <Checkbox checked={!!sp.auto_queue} disabled={saving} onCheckedChange={(v) => saveSp({ auto_queue: !!v })} className="mt-0.5 h-4 w-4 rounded-none border-industrial" data-testid="silent-print-auto-toggle" />
              <span className="mono text-[11px] text-muted-industrial uppercase leading-relaxed">Auto-queue every completed load. Leave off to print on demand only — the <b className="text-ink">Print</b> button on the Dockets page.</span>
            </label>
            <div className="mt-3 flex flex-wrap items-center gap-3 mono text-[10px] uppercase tracking-widest" data-testid="silent-print-agent">
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 border ${agentOk ? "border-emerald-500 text-emerald-400" : "border-industrial text-muted-industrial"}`}><span className={`w-2 h-2 rounded-full ${agentOk ? "bg-emerald-400 animate-pulse" : "bg-muted-industrial"}`} />{agentOk ? `Print service online · ${sp.agent?.host || ""}` : seen !== null ? `Print service last seen ${Math.round(seen / 60)} min ago` : "Print service not connected yet"}</span>
              <span className="text-muted-industrial">Install: plant-agent/install-print-service-windows.bat · Canon GX3060 guide: plant-agent/CANON-GX3060-SETUP.md</span>
            </div>
            {jobs.length > 0 && (
              <table className="w-full mono text-[11px] mt-3" data-testid="silent-print-jobs">
                <tbody>
                  {jobs.slice(0, 10).map((j) => (
                    <tr key={j.id} className="border-t border-industrial align-top">
                      <td className="py-1.5 pr-2 font-bold whitespace-nowrap">{j.docket_no}{j.test ? " (test)" : ""}</td>
                      <td className="py-1.5 pr-2 text-muted-industrial whitespace-nowrap">{new Date(j.created_at).toLocaleTimeString()}</td>
                      <td className="py-1.5 pr-2 w-full">
                        <span className={`uppercase font-bold ${j.status === "printed" ? "text-emerald-400" : j.status === "failed" ? "text-red-400" : "text-amber-400"}`}>{j.status}</span>
                        {j.attempts > 1 && <span className="text-muted-industrial"> · {j.attempts} attempts</span>}
                        {j.error && <div className="text-red-400 normal-case mt-0.5 break-all">{j.error}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RestoreBackupCard() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const toB64 = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(f); });
  const run = async (commit) => {
    if (!file) return toast.error("Choose a backup file first");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/backup", { file_b64: await toB64(file), file_name: file.name, commit });
      if (commit) {
        const st = data.stats || Object.fromEntries(Object.entries(data.report || {}).map(([k, v]) => [k, v.created]));
        const created = Object.entries(st).filter(([k, v]) => /created/.test(k) ? v : false);
        toast.success(`Restore complete · ${created.map(([k, v]) => `${v} ${k.replace("_created", "")}`).join(", ") || "nothing new to add"}`, { duration: 12000 });
        setPreview(null); setFile(null);
      } else setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); } finally { setBusy(false); }
  };
  const rows = preview ? Object.entries(preview.stats || preview.counts || {}) : [];
  return (
    <div className="card-industrial p-5" data-testid="restore-backup-card">
      <div className="label-tag flex items-center gap-1.5"><Database className="w-3 h-3" /> Data Restore</div>
      <div className="heading text-xl font-black uppercase mt-1">Restore from Backup</div>
      <div className="text-xs text-muted-industrial mt-2 max-w-2xl">
        Upload a legacy Dispatch <b>Full Backup .html</b> (from your old site) or an ArmorBatch <b>backup.json / .json.gz</b>. Records are matched by name / docket no. / invoice no. and only added or updated — nothing is deleted and passwords are never changed. Preview first, then commit.
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <input ref={fileRef} type="file" accept=".html,.htm,.json,.gz" className="hidden" data-testid="restore-file" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
        <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-none border-industrial uppercase text-xs" data-testid="restore-choose-file"><Upload className="w-3.5 h-3.5 mr-1" /> {file ? file.name : "Choose backup file"}</Button>
        <Button variant="outline" onClick={() => run(false)} disabled={!file || busy} className="rounded-none border-industrial uppercase text-xs" data-testid="restore-preview-btn">Preview</Button>
        <Button onClick={() => run(true)} disabled={!preview || busy} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold" data-testid="restore-commit-btn">{busy ? "Working…" : "Restore now"}</Button>
      </div>
      {preview && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="restore-preview">
          {rows.map(([k, v]) => (
            <div key={k} className="border border-industrial px-3 py-2">
              <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">{k.replace(/_/g, " ")}</div>
              <div className={`mono text-lg font-bold ${/created/.test(k) ? "text-emerald-400" : /updated/.test(k) ? "text-sky-400" : "text-ink"}`}>{v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TeamMembersCard({ isAdmin, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const { setUser, refreshOrg } = useAuth();
  const navigate = useNavigate();

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const onLoginAs = async (u) => {
    if (!window.confirm(`Log in as ${u.name} (${u.role})? You'll get a yellow bar to return to your own account.`)) return;
    try {
      const { data } = await api.post(`/auth/users/${u.id}/impersonate`);
      setUser(data);
      await refreshOrg();
      toast.success(`Now viewing as ${data.name}`);
      navigate(data.role === "driver" ? "/driver" : data.role === "loader" ? "/loader" : "/");
    } catch (e) { toast.error(e.response?.data?.detail || "Login as failed"); }
  };

  return (
    <div className="card-industrial overflow-x-auto" data-testid="team-members-card">
      <div className="p-5 border-b border-industrial flex items-center justify-between">
        <div>
          <div className="label-tag">Team Members</div>
          <div className="text-xs text-muted-industrial mt-0.5">
            Each user has their own login and password.{isAdmin ? " You can add, edit, reset passwords, or remove users below." : ""}
          </div>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="add-user-btn"
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"
          >
            <UserPlus className="w-4 h-4 mr-1" /> Add User
          </Button>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="border-b border-industrial">
          <tr className="text-left">
            <th className="p-3 label-tag">Name</th>
            <th className="p-3 label-tag">Email</th>
            <th className="p-3 label-tag">Role</th>
            {isAdmin && <th className="p-3 label-tag text-right">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-industrial" data-testid={`user-row-${u.email}`}>
              <td className="p-3">{u.name}</td>
              <td className="p-3 mono text-xs">{u.email}</td>
              <td className="p-3 mono text-xs uppercase text-muted-industrial">
                {ROLES.find((r) => r.key === u.role)?.label || u.role?.replace("_", " ")}
                {Array.isArray(u.allowed_pages) && <span className="ml-2 text-[9px] px-1 border border-primary-orange text-primary-orange" title={`${u.allowed_pages.length} nominated sections`}>CUSTOM</span>}
              </td>
              {isAdmin && (
                <td className="p-3 text-right space-x-3">
                  {u.id !== currentUserId && u.role !== "customer" && (
                    <button
                      type="button"
                      onClick={() => onLoginAs(u)}
                      data-testid={`login-as-${u.email}`}
                      className="text-amber-500 hover:underline inline-flex items-center gap-1 text-xs uppercase mono"
                    >
                      <UserCog className="w-3.5 h-3.5" /> Login as
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditUser(u)}
                    data-testid={`edit-user-${u.email}`}
                    className="text-primary-orange hover:underline inline-flex items-center gap-1 text-xs uppercase mono"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setResetUser(u)}
                    data-testid={`reset-password-${u.email}`}
                    className="text-primary-orange hover:underline inline-flex items-center gap-1 text-xs uppercase mono"
                  >
                    <KeyRound className="w-3.5 h-3.5" /> Reset PW
                  </button>
                  {u.id !== currentUserId && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
                        try {
                          await api.delete(`/auth/users/${u.id}`);
                          toast.success("User deleted");
                          load();
                        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                      }}
                      data-testid={`delete-user-${u.email}`}
                      className="text-red-500 hover:underline inline-flex items-center gap-1 text-xs uppercase mono"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Del
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
          {users.length === 0 && (
            <tr><td colSpan={isAdmin ? 4 : 3} className="p-6 text-center text-muted-industrial">
              {isAdmin ? "No users yet — click Add User to invite the first team member." : "Requires admin/plant manager role."}
            </td></tr>
          )}
        </tbody>
      </table>

      <AddUserDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
      <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onSaved={load} />
      <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} />
    </div>
  );
}

function AddUserDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", role: "driver", password: "", phone: "", allowed_pages: null });
  const [saving, setSaving] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ name: "", email: "", role: "driver", password: generatePassword(), allowed_pages: null });
      setCreatedPassword(null);
    }
  }, [open]);

  const save = async () => {
    if (!form.email || !form.name) return toast.error("Name and email are required");
    if ((form.password || "").length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    try {
      await api.post("/auth/users", form);
      toast.success(`${form.email} created`);
      setCreatedPassword({ email: form.email, password: form.password });
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const copyCreds = () => {
    if (!createdPassword) return;
    navigator.clipboard.writeText(`Email: ${createdPassword.email}\nPassword: ${createdPassword.password}`);
    toast.success("Credentials copied");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid="add-user-dialog">
        <DialogHeader><DialogTitle className="heading uppercase">Add Team Member</DialogTitle></DialogHeader>
        {createdPassword ? (
          <div className="space-y-3">
            <div className="border border-primary-orange bg-primary-orange/10 p-3">
              <div className="label-tag text-[10px] mb-2 text-primary-orange">User created — hand these to them now</div>
              <div className="mono text-sm space-y-1">
                <div><span className="text-muted-industrial">Email:</span> <b>{createdPassword.email}</b></div>
                <div><span className="text-muted-industrial">Password:</span> <b>{createdPassword.password}</b></div>
              </div>
              <div className="text-[10px] mono text-muted-industrial mt-2">
                They can change it themselves via Settings → Change Password after logging in.
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={copyCreds} className="rounded-none"><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>
              <Button onClick={() => onOpenChange(false)} className="rounded-none bg-primary-orange">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-user-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} data-testid="add-user-email" />
            </div>
            <div>
              <Label>Mobile <span className="text-muted-industrial normal-case font-normal">· drivers get a text if GPS goes dark</span></Label>
              <Input type="tel" placeholder="04xx xxx xxx" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="add-user-phone" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, allowed_pages: null })}>
                <SelectTrigger data-testid="add-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-industrial mt-1">{ROLES.find((r) => r.key === form.role)?.hint}</div>
            </div>
            <SectionPicker role={form.role} value={form.allowed_pages} onChange={(v) => setForm({ ...form, allowed_pages: v })} />
            <div>
              <Label>Initial Password</Label>
              <div className="flex gap-2">
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="add-user-password" />
                <Button type="button" variant="outline" className="rounded-none" onClick={() => setForm({ ...form, password: generatePassword() })} title="Regenerate">
                  <RefreshCcw className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="text-[10px] text-muted-industrial mt-1">
                Min 8 characters. Share this with the user; they can change it later.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="add-user-save" className="rounded-none bg-primary-orange">
                {saving ? "Creating…" : "Create"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user: target, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("driver");
  const [pages, setPagesState] = useState(null);
  const pagesRef = useRef(null);
  const setPages = (v) => { pagesRef.current = v; setPagesState(v); };
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (target) { setName(target.name || ""); setPhone(target.phone || ""); setRole(target.role || "driver"); setPages(Array.isArray(target.allowed_pages) ? target.allowed_pages : null); }
  }, [target]);
  if (!target) return null;
  const save = async () => {
    setSaving(true);
    const p = pagesRef.current;
    try {
      await api.patch(`/auth/users/${target.id}`, { name, phone, role, allowed_pages: p, reset_pages: p === null });
      toast.success("User updated");
      await onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle className="heading uppercase">Edit <span className="mono text-primary-orange">{target.email}</span></DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" /></div>
          <div><Label>Mobile <span className="text-muted-industrial normal-case font-normal">· dark-GPS text</span></Label><Input type="tel" placeholder="04xx xxx xxx" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="edit-user-phone" /></div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => { setRole(v); setPages(null); }}>
              <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-muted-industrial mt-1">{ROLES.find((r) => r.key === role)?.hint}</div>
          </div>
          <SectionPicker role={role} value={pages} onChange={setPages} />
          <DialogFooter>
            <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="edit-user-save" className="rounded-none bg-primary-orange">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user: target, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { if (target) { setPw(generatePassword()); setDone(null); } }, [target]);
  if (!target) return null;
  const save = async () => {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    setSaving(true);
    try {
      await api.post(`/auth/users/${target.id}/reset-password`, { new_password: pw });
      setDone({ email: target.email, password: pw });
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setSaving(false); }
  };
  const copy = () => {
    if (!done) return;
    navigator.clipboard.writeText(`Email: ${done.email}\nPassword: ${done.password}`);
    toast.success("Credentials copied");
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="heading uppercase">Reset Password · <span className="mono text-primary-orange">{target.email}</span></DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-3">
            <div className="border border-primary-orange bg-primary-orange/10 p-3">
              <div className="label-tag text-[10px] mb-2 text-primary-orange">New password saved — hand it to them now</div>
              <div className="mono text-sm">
                <div><span className="text-muted-industrial">Email:</span> <b>{done.email}</b></div>
                <div><span className="text-muted-industrial">New password:</span> <b>{done.password}</b></div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={copy} className="rounded-none"><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>
              <Button onClick={onClose} className="rounded-none bg-primary-orange">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-muted-industrial">
              Setting a new password will invalidate the current one. The user will need this new password to log in next time.
            </div>
            <div>
              <Label>New Password</Label>
              <div className="flex gap-2">
                <Input value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
                <Button type="button" variant="outline" className="rounded-none" onClick={() => setPw(generatePassword())} title="Regenerate">
                  <RefreshCcw className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="reset-password-save" className="rounded-none bg-primary-orange">
                {saving ? "Saving…" : "Save New Password"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
